import type {
  ProjectContextComposeLearnedItem,
  ProjectContextTerm,
} from '@ai-meeting/shared'
import {
  ArrowUturnLeftIcon,
  PencilIcon,
  SparklesIcon,
  TrashIcon,
} from '@/components/Icons'
import { useUiStrings } from '@/hooks/useUiStrings'

export type DictionaryRow =
  | {
      kind: 'manual'
      key: string
      term: ProjectContextTerm
    }
  | {
      kind: 'learned'
      key: string
      learned: ProjectContextComposeLearnedItem
      blocked: boolean
    }

type Props = {
  rows: DictionaryRow[]
  onEditManual?: (term: ProjectContextTerm) => void
  onDeleteManual?: (term: ProjectContextTerm) => void
  /** Writes a learned term into the declared half, keeping its translation. */
  onAddLearned?: (learned: ProjectContextComposeLearnedItem) => void
  onBlockLearned?: (learned: ProjectContextComposeLearnedItem) => void
  onUnblockLearned?: (learned: ProjectContextComposeLearnedItem) => void
  disabled?: boolean
}

const iconButtonClass =
  'flex items-center justify-center rounded border p-1 transition-colors disabled:opacity-50'

/**
 * Declared + AI-learned dictionary rows for the project dictionary sub-tab.
 * Columns match Figma: term (JA), translation (VI), source, actions.
 */
export default function ProjectDictionaryTermsTable({
  rows,
  onEditManual,
  onDeleteManual,
  onAddLearned,
  onBlockLearned,
  onUnblockLearned,
  disabled = false,
}: Props) {
  const { t } = useUiStrings()

  if (rows.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-[var(--text-tertiary)]">
        {t.projectDetailDictNoMatches}
      </p>
    )
  }

  return (
    <div className="w-full overflow-x-auto" data-testid="project-dictionary-terms">
      <table className="w-full min-w-[640px] border-collapse text-left text-xs">
        <thead>
          <tr className="border-b border-[var(--border-color)] font-medium text-[var(--text-tertiary)]">
            <th className="w-[28%] py-2.5">{t.projectDetailDictColTerm}</th>
            <th className="w-[32%] py-2.5">{t.projectDetailDictColTarget}</th>
            <th className="w-[22%] py-2.5">{t.projectDetailDictColSource}</th>
            <th className="py-2.5 text-right">{t.projectDetailDictColActions}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[var(--border-color)]">
          {rows.map((row) => {
            if (row.kind === 'manual') {
              const { term } = row
              return (
                <tr key={row.key} className="transition-colors hover:bg-[var(--bg-primary)]/50">
                  <td className="py-4 pr-4 font-semibold text-[var(--text-primary)]">
                    {term.source}
                  </td>
                  <td className="py-4 pr-4 text-[var(--text-secondary)]">
                    {term.target ?? '—'}
                  </td>
                  <td className="py-4 pr-4 text-[var(--text-secondary)]">
                    {t.projectDetailDictSourceManual}
                  </td>
                  <td className="py-4">
                    <div className="flex items-center justify-end gap-1.5">
                      <button
                        type="button"
                        disabled={disabled}
                        aria-label={t.projectDetailDictEditTerm}
                        onClick={() => onEditManual?.(term)}
                        className={`${iconButtonClass} border-[var(--border-color)] text-[var(--text-secondary)] hover:border-[var(--border-hover)] hover:text-[var(--text-primary)]`}
                      >
                        <PencilIcon className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        disabled={disabled}
                        aria-label={t.projectDetailDictDeleteTerm}
                        onClick={() => onDeleteManual?.(term)}
                        className={`${iconButtonClass} border-rose-200 text-red-500 hover:border-rose-300 dark:border-rose-500/40 dark:text-rose-400`}
                      >
                        <TrashIcon className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              )
            }

            const { learned, blocked } = row
            return (
              <tr key={row.key} className="transition-colors hover:bg-[var(--bg-primary)]/50">
                <td className="py-4 pr-4 font-semibold text-[var(--text-primary)]">
                  {learned.source}
                </td>
                <td className="py-4 pr-4 text-[var(--text-secondary)]">
                  {learned.target ?? '—'}
                </td>
                <td className="py-4 pr-4">
                  <span className="inline-flex items-center gap-1 rounded bg-[#f5f3ff] px-2 py-0.5 text-[11px] font-medium text-[var(--accent)] dark:bg-violet-500/15 dark:text-violet-300">
                    <SparklesIcon className="h-3 w-3 shrink-0" />
                    {t.projectDetailDictSourceAi}
                  </span>
                </td>
                <td className="py-4">
                  <div className="flex items-center justify-end gap-1.5">
                    <button
                      type="button"
                      disabled={disabled}
                      aria-label={t.projectDetailDictReviewAdd}
                      onClick={() => onAddLearned?.(learned)}
                      className={`${iconButtonClass} border-[var(--border-color)] text-[var(--text-secondary)] hover:border-[var(--border-hover)] hover:text-[var(--text-primary)]`}
                    >
                      <PencilIcon className="h-3.5 w-3.5" />
                    </button>
                    {blocked ? (
                      <button
                        type="button"
                        disabled={disabled}
                        aria-label={t.projectDetailDictUnblock}
                        onClick={() => onUnblockLearned?.(learned)}
                        className={`${iconButtonClass} border-[var(--border-color)] text-[var(--text-secondary)] hover:border-[var(--border-hover)] hover:text-[var(--text-primary)]`}
                      >
                        <ArrowUturnLeftIcon className="h-3.5 w-3.5" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        disabled={disabled}
                        aria-label={t.projectDetailDictBlock}
                        onClick={() => onBlockLearned?.(learned)}
                        className={`${iconButtonClass} border-rose-200 text-red-500 hover:border-rose-300 dark:border-rose-500/40 dark:text-rose-400`}
                      >
                        <TrashIcon className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
